<script setup lang="ts">
/**
 * Customer tab — the lean panel (owner 17 ต.ค. 69, approved mockups LeanNew / LeanReturning: "เก็บไว้ในแผงเฉพาะสิ่งที่
 * ช่วยขาย หรือช่วยแก้ปัญหาลูกค้า · ที่เหลือไม่ได้ลบทิ้ง ย้ายไปเมนู “…”"):
 *   header        name · channel · ลูกค้าใหม่/เดิม · allergy / preference chips · team tags · phone · แนวลูกค้า ·
 *                 returning: "สั่ง N ครั้ง · ยอดรวม · ล่าสุด X วันก่อน" + "เลยรอบปกติ" · POS link · latest internal note
 *   ขายต่อ        SellCard (stage + what is known / missing · upsell · สั่งชุดเดิมซ้ำ · AI cart · ทักตาม)
 *   ปัญหา/ติดตาม  ProblemCard, only when there is something (deliveries on their way · script problems · payment tag)
 *   then the web chat card and the old-chat history cards as before.
 * Moved out: tag editing, web chat block, "ล้างแชทนี้ (ทดสอบ)", adding a note → the chat header's "…" menu
 * (chat/ChatMoreMenu.vue) · "โอกาสปิดการขาย" + "ขั้นต่อไปที่ AI แนะนำ" → AiInsightBlock.vue (kept, not shown).
 */
import { computed, ref, watch } from 'vue'
import type { ConversationDetail } from '../../api/types'
import { autoTagTitle, isReturningAuto } from '../../lib/autoTags'
import { formatAgo } from '../../lib/inbox'
import { firstSeenText, headerChips, isProblemTag, overdueChip, statsLine, statsOf } from '../../lib/script'
import { styleLine } from '../../lib/style'
import { useClock } from '../../stores/clock'
import { useCollab } from '../../stores/collab'
import { useInbox } from '../../stores/inbox'
import { useOrders } from '../../stores/order'
import { usePhoneLink } from '../../stores/phonelink'
import { useScript } from '../../stores/script'
import { useWebchat } from '../../stores/webchat'
import ContactNotes from '../collab/ContactNotes.vue'
import ChannelBadge from '../ui/ChannelBadge.vue'
import Icon from '../ui/Icon.vue'
import FbHistoryCard from './FbHistoryCard.vue'
import LineHistoryCard from './LineHistoryCard.vue'
import ProblemCard from './ProblemCard.vue'
import SellCard from './SellCard.vue'
import WebVisitorCard from './WebVisitorCard.vue'

const props = defineProps<{ detail: ConversationDetail }>()
const store = useInbox()
const clock = useClock()
const script = useScript()
const collab = useCollab()

watch(
  () => props.detail.id,
  (id) => script.ensure(id),
  { immediate: true },
)

const pos = computed(() => props.detail.pos)
// The POS link as the order tab knows it (GET /pos/conversations/:id + the linked customer, masked number):
// the detail API sends `pos: null` / `phone_masked: null` (no POS call in list/detail), so the card reads it here.
const orders = useOrders()
const phoneLink = usePhoneLink()
watch(
  () => props.detail.id,
  (id) => phoneLink.ensure(id),
  { immediate: true },
)
const linkedId = computed(() => orders.contexts[props.detail.id]?.customer_id ?? null)
const linked = computed(() => (linkedId.value ? (orders.customers[linkedId.value] ?? null) : null))
watch(linkedId, () => phoneLink.ensure(props.detail.id)) // linked just now (order tab, phone button): load it
const phone = computed(() => props.detail.contact.phone_masked ?? linked.value?.mobile_masked ?? null)
// web chat: the phone the visitor typed in the chat window (no POS link yet)
const webchat = useWebchat()
const webPhone = computed(() => (props.detail.channel === 'web' ? (webchat.visitors[props.detail.id]?.phone ?? null) : null))

const sc = computed(() => script.scripts[props.detail.id] ?? null)
const segment = computed(() => sc.value?.segment ?? pos.value?.segment ?? null)
const chips = computed(() => headerChips(sc.value?.known ?? [], pos.value?.allergies))
/** Team tags + system tags that are not a problem (those are in "ปัญหา / ติดตาม"). Editing: the "…" menu.
 *  A "ลูกค้าใหม่" / "ลูกค้าเก่า" label the team put on is the segment chip already — and so is the system's ลูกค้าเก่า
 *  (api/app/collab/autotags.py) next to the "ลูกค้าเก่า" chip: the word shows once. */
const tags = computed(() =>
  (props.detail.tags ?? []).filter(
    (t) =>
      !isProblemTag(t) &&
      !(segment.value && segNames.value.includes(t.name.trim())) &&
      !(segment.value === 'returning' && isReturningAuto(t)),
  ),
)
const segLabel = computed(() => (segment.value === 'returning' ? 'ลูกค้าเก่า' : 'ลูกค้าใหม่'))
/** Label names that say what the chip says — for the returning chip also its old word "ลูกค้าเดิม" (owner 18 ต.ค. 69
 *  "เปลี่ยนจากคำว่า ลูกค้าเดิม เป็น ลูกค้าเก่า ทั้งหมด"), so a team label of that name stays hidden next to it as before. */
const segNames = computed(() => (segment.value === 'returning' ? [segLabel.value, 'ลูกค้าเดิม'] : [segLabel.value]))
const stats = computed(() => statsOf(sc.value?.returning, pos.value?.stats))
const statsText = computed(() => (stats.value ? statsLine(stats.value, clock.now) : null))
const overdue = computed(() => (stats.value ? overdueChip(stats.value, clock.now) : null))
const firstSeen = computed(() => (segment.value === 'returning' ? null : firstSeenText(props.detail.contact.first_seen_at, clock.now)))
/** แนวลูกค้า (lib/style.ts): how the customer writes + what they said they care about — rules, no AI. */
const style = computed(() => (sc.value?.style ? styleLine(sc.value.style) : null))

// the newest internal note inline; all of them (add / edit) one tap away
const notes = computed(() => collab.notesFor(props.detail.id))
const latest = computed(() => notes.value[0] ?? null)
const notesOpen = ref(false)
watch(
  () => props.detail.id,
  () => (notesOpen.value = false),
)

/** Search / link in the order tab (the customer block there searches POS by phone or name). */
function findInPos(): void {
  store.panelTab = 'order'
}
</script>

<template>
  <div class="pane" role="tabpanel" aria-label="ลูกค้า">
    <div class="pn-body scroll">
      <section class="lh" aria-label="ข้อมูลลูกค้า">
        <div class="cus-name">
          <span class="cus-n">{{ detail.contact.name }}</span>
          <ChannelBadge :channel="detail.channel" :kind="detail.kind" />
          <span v-if="segment" class="seg" :class="segment === 'returning' ? 'seg-ret' : 'seg-new'">{{ segLabel }}</span>
        </div>
        <div v-if="chips.length || tags.length" class="cus-tags" aria-label="ป้ายลูกค้า">
          <span v-for="c in chips" :key="c.text" class="flag" :class="c.tone" :title="c.title ?? undefined"
            ><Icon v-if="c.tone === 'crit'" name="alert" />{{ c.text }}</span
          >
          <span v-for="t in tags" :key="t.id" class="tg lg" :class="'tg-' + t.tone" :title="autoTagTitle(t, clock.now) ?? undefined">{{
            t.name
          }}</span>
        </div>
        <div class="lh-sub">
          <span v-if="detail.contact.username" class="cus-ph">@{{ detail.contact.username }}</span>
          <span v-if="phone" class="cus-ph mono">{{ phone }}</span>
          <span v-else-if="webPhone" class="cus-ph mono">{{ webPhone }}</span>
          <span v-else-if="detail.channel !== 'web'" class="cus-ph">ยังไม่มีเบอร์โทร</span>
        </div>
        <p v-if="style" class="lh-style" :title="style.title">
          <Icon name="bubble" /><span
            ><span class="lh-sl">แนวลูกค้า:</span> <b>{{ style.label }}</b
            ><template v-if="style.motives"> · {{ style.motives }}</template></span
          >
        </p>

        <!-- returning: the numbers on one line + later than usual -->
        <p v-if="statsText" class="lh-stats">
          <span>{{ statsText }}</span>
          <a v-if="pos" class="lh-pos" :href="pos.url" target="_blank" rel="noopener noreferrer" :aria-label="`เปิดโปรไฟล์${detail.contact.name}ใน POS`"
            >เปิดใน POS<Icon name="external"
          /></a>
          <span v-if="overdue" class="warn-chip" :title="overdue.title ?? undefined"><Icon name="clock" />{{ overdue.text }}</span>
        </p>

        <div v-if="pos && !statsText" class="pos-link">
          <Icon name="checkCircle" /><b>ผูก POS แล้ว</b><span class="grow"></span>
          <a class="pl-open" :href="pos.url" target="_blank" rel="noopener noreferrer" :aria-label="`เปิดโปรไฟล์${detail.contact.name}ใน POS`"
            ><span>เปิดใน POS</span><Icon name="external"
          /></a>
        </div>
        <div v-else-if="!pos && linkedId" class="pos-link">
          <Icon name="checkCircle" /><b>ผูก POS แล้ว</b><span v-if="linked" class="pl-name">{{ linked.name }}</span>
          <span class="grow"></span>
          <button class="pl-open" @click="store.panelTab = 'history'"><span>ดูประวัติ</span></button>
        </div>
        <div v-else-if="!pos" class="pos-link none">
          <span class="pl-t"><template v-if="firstSeen">{{ firstSeen }} · </template>ยังไม่ผูกกับลูกค้าใน POS</span>
          <span class="grow"></span>
          <button class="pl-open" @click="findInPos"><Icon name="search" /><span>ค้นหาใน POS</span></button>
        </div>

        <!-- the newest internal note (adding one: "…" in the chat header, or โน้ตภายใน above the typing box) -->
        <div v-if="latest" class="lh-note">
          <p class="lh-nt"><span class="lh-nl">โน้ต:</span> {{ latest.text }}</p>
          <span class="lh-nm">{{ latest.author.name }} · {{ formatAgo(latest.at, clock.now) }}</span>
          <button type="button" class="lh-nb" :aria-expanded="notesOpen" @click="notesOpen = !notesOpen">
            {{ notesOpen ? 'ย่อ' : notes.length > 1 ? `ดูโน้ตทั้งหมด ${notes.length}` : 'แก้ไข' }}
          </button>
        </div>
      </section>
      <ContactNotes v-if="notesOpen" :detail="detail" />

      <!-- the owner-approved mockup (LeanReturning) puts an open problem first: it only shows when there is one -->
      <ProblemCard :detail="detail" />
      <SellCard v-if="!detail.is_history" :detail="detail" />

      <WebVisitorCard v-if="detail.channel === 'web'" :conv-id="detail.id" />
      <!-- older Messenger / Instagram conversation pulled once (api/app/fb_history.py) -->
      <FbHistoryCard v-if="(detail.channel === 'fb' || detail.channel === 'ig') && detail.kind === 'dm' && !detail.is_history" :detail="detail" />
      <!-- old chat from the LINE OA export, linked by phone / confirmed by staff (api/app/line_link.py) -->
      <LineHistoryCard v-if="detail.channel === 'line' && detail.kind === 'dm' && !detail.is_history" :detail="detail" />
      <div class="lh-end" aria-hidden="true"></div>
    </div>
  </div>
</template>

<style scoped>
.lh {
  padding: 14px 16px;
  border-bottom: 1px solid var(--line);
  display: flex;
  flex-direction: column;
  gap: 7px;
}
.cus-name {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
  min-width: 0;
}
.cus-n {
  min-width: 0;
  font-size: 17px;
  font-weight: 700;
  line-height: 26px;
  overflow-wrap: anywhere;
}
.seg {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 9px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  white-space: nowrap;
}
.seg-new {
  background: #e5effa;
  color: #1d4f8f;
}
.seg-ret {
  background: var(--acs);
  color: var(--ac2);
}
.cus-tags {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.flag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 24px;
  padding: 0 9px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  white-space: nowrap;
}
.flag.crit {
  padding-left: 7px;
  background: var(--crits);
  color: #a61b12;
}
.flag.mut {
  background: #edf1ef;
  color: var(--mut);
}
.flag :deep(.ic) {
  width: 13px;
  height: 13px;
  stroke-width: 2;
}
.tg.lg {
  height: 24px;
  padding: 0 8px;
  font-size: 12px;
  font-weight: 600;
  line-height: 24px;
}
.lh-sub {
  display: flex;
  flex-wrap: wrap;
  column-gap: 12px;
}
.lh-sub:empty {
  display: none;
}
/* แนวลูกค้า: one quiet line (the label in ink, the rest muted) */
.lh-style {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 12.5px;
  line-height: 19px;
  color: var(--mut);
}
.lh-style > :deep(.ic) {
  flex: none;
  width: 14px;
  height: 14px;
  margin-top: 2.5px;
  color: #7c8f86;
}
.lh-style b {
  color: var(--ink);
  font-weight: 600;
}
.cus-ph {
  font-size: 13px;
  color: var(--mut);
  line-height: 20px;
  letter-spacing: 0.3px;
}
.lh-stats {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
  font-size: 12.5px;
  line-height: 19px;
  color: var(--mut);
}
.lh-pos {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  color: var(--ac);
  font-weight: 600;
  text-decoration: none;
}
.lh-pos:hover {
  color: var(--ac2);
  text-decoration: underline;
}
.lh-pos :deep(.ic) {
  width: 13px;
  height: 13px;
}
.pos-link {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 34px;
  padding: 0 4px 0 10px;
  border-radius: 9px;
  background: var(--acs);
  font-size: 12.5px;
  line-height: 19px;
  color: var(--ac2);
}
.pos-link > :deep(.ic) {
  width: 16px;
  height: 16px;
}
.pos-link b {
  font-weight: 600;
  white-space: nowrap;
}
.pl-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--mut);
}
.pos-link.none {
  background: #f4f6f5;
  color: var(--mut);
}
.pl-t {
  min-width: 0;
  padding: 7px 0;
}
.pl-open {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 28px;
  padding: 0 9px;
  border-radius: 7px;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 19px;
  color: var(--ac2);
  text-decoration: none;
  white-space: nowrap;
}
.pl-open:hover {
  background: rgba(14, 122, 92, 0.08);
}
.pos-link.none .pl-open {
  border: 1px solid #c3cdc8;
  background: #fff;
  color: var(--ink);
}
.pl-open :deep(.ic) {
  width: 14px;
  height: 14px;
}
.lh-note {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 2px 8px;
  padding: 6px 10px;
  border-radius: 8px;
  background: #f4f6f5;
}
.lh-nt {
  flex: 1 1 100%;
  font-size: 13px;
  line-height: 20px;
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.lh-nl {
  color: var(--mut);
}
.lh-nm {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.lh-nb {
  margin-left: auto;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  color: var(--ac2);
}
.lh-nb:hover {
  text-decoration: underline;
}
.lh-end {
  height: 12px;
}
</style>
